import { ReactNode, MutableRefObject, useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import styled, { css } from 'styled-components';

import { ClientOnlyPortal } from 'shared/components/ClientOnlyPortal';
import { ConditionalWrapper } from 'shared/components/ConditionalWrapper';
import { IWindowPosition } from 'store/types/pdpStateTypes';
import { setWindowFocus } from 'store/slice/pdpStateSlice';
import { useOutsideClick } from 'shared/hooks/useOutsideClick';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { WindowHeader } from 'shared/components/Window/WindowHeader';

export interface IWindowProps {
  'data-qa'?: string;
  children: ReactNode;
  className?: string;
  closeType?: 'icon' | 'text';
  disableDrag?: boolean;
  disableOutsideClick?: boolean;
  id: string;
  initialPermanentPosition?: IWindowPosition;
  isCrawler?: boolean;
  isHovering?: boolean;
  isPdp?: boolean;
  isPermanent: boolean;
  isSubtitleOnNewLine?: boolean;
  modalRef?: MutableRefObject<HTMLDivElement>;
  onClick?: () => void;
  onClose?: (val?: any) => void;
  onWheel?: (e: any) => void;
  style?: { [key: string]: string | number };
  subtitle?: string | ReactNode;
  title?: string | ReactNode;
  width?: string;
  xButtonSize?: number;
}

export const Window = ({
  children,
  className,
  closeType = 'icon',
  'data-qa': dataQa,
  disableOutsideClick = false,
  id,
  isHovering = false,
  isPdp = true,
  isCrawler = false,
  isPermanent,
  disableDrag = false,
  initialPermanentPosition,
  isSubtitleOnNewLine = false,
  modalRef,
  onClick,
  onClose,
  onWheel,
  subtitle,
  style,
  title,
  width,
  xButtonSize,
}: IWindowProps) => {
  const state = useStoreSelector((store) => store.pdpState);
  const storeDispatch = useStoreDispatch();
  const focusedWindow = state?.focusedWindow ?? id;
  const { isMobile } = useWindowSize();
  const windowRef = useOutsideClick(onClose, !isMobile || disableOutsideClick);

  // We can always assume a window is newly focused on first render
  // but after that we need to check if another window is focused
  const [newlyFocused, setNewlyFocused] = useState(true);

  useEffect(() => {
    if (isPermanent && focusedWindow !== id) setNewlyFocused(false);
  }, [focusedWindow]);

  useEffect(() => {
    if (isPermanent && isPdp) {
      storeDispatch(setWindowFocus(id));
    }
  }, [isPermanent, isPdp]);

  const handleDragStart = () => {
    if (isPdp) {
      storeDispatch(setWindowFocus(id));
    }
  };

  const handleClose = (e) => {
    if (isPermanent) {
      e.stopPropagation();
      if (onClose) onClose();
    }
  };

  const mergeRef = (el) => {
    if (modalRef) {
      modalRef.current = el;
    }
    windowRef.current = el;
  };

  return (
    <ConditionalWrapper
      if={!isMobile && isPdp && !isCrawler}
      with={(_children) => (
        <ClientOnlyPortal selector="#pdp-wrapper">
          <Draggable
            bounds="#pdp-wrapper"
            cancel='div[class^="WindowItemShortText"], div[class^="WindowItemLongText"], div[class^="WindowItemTwoColumns"], button, img, input'
            onStart={handleDragStart}
            disabled={disableDrag || !isPermanent}
          >
            {_children}
          </Draggable>
        </ClientOnlyPortal>
      )}
    >
      <Window.Window
        className={className}
        data-qa={dataQa}
        ref={mergeRef}
        onClick={onClick}
        onWheel={onWheel}
        style={style}
        $isHovering={isPdp && isHovering && !isPermanent}
        $isFocused={focusedWindow === id}
        $newlyFocused={isPdp && newlyFocused}
        $width={width}
        $initialPermanentPosition={initialPermanentPosition}
      >
        {title && (
          <WindowHeader
            closeType={closeType}
            isPermanent={isPermanent}
            disableDrag={disableDrag}
            isSubtitleOnNewLine={isSubtitleOnNewLine}
            title={title}
            subtitle={subtitle}
            handleClose={handleClose}
            xButtonSize={xButtonSize}
          />
        )}
        {children}
      </Window.Window>
    </ConditionalWrapper>
  );
};

Window.Window = styled.div<{
  $width?: string;
  $isFocused: boolean;
  $newlyFocused?: boolean;
  $isHovering?: boolean;
  $initialPermanentPosition: IWindowPosition;
}>`
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};
  border: ${({ theme }) => theme.borders.black};
  display: flex;
  flex-direction: column;
  float: left;
  pointer-events: auto;
  z-index: ${({ theme }) => theme.zIndex.window};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
    bottom: 0;
    height: fit-content;
    left: 0;
    margin: auto;
    position: fixed;
    right: 0;
    top: 0;
    width: ${({ $width }) => $width || 'unset'};
    max-width: 335px;
    max-height: 300px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: ${({ $width }) => $width || '300px'};
  }

  ${({ $isFocused, theme }) =>
    $isFocused &&
    css`
      z-index: ${theme.zIndex.pdpWindowFocused};
    `};

  ${({ $newlyFocused, $isHovering, theme }) =>
    ($newlyFocused || $isHovering) &&
    css`
      z-index: ${theme.zIndex.pdpWindowNewlyFocused};
    `};

  ${({ $initialPermanentPosition }) =>
    $initialPermanentPosition &&
    css`
      position: absolute;
      left: ${$initialPermanentPosition.left ? `${$initialPermanentPosition.left}px` : 'auto'};
      bottom: ${$initialPermanentPosition.bottom
        ? `${$initialPermanentPosition.bottom}px`
        : 'auto'};
      top: ${$initialPermanentPosition.top ? `${$initialPermanentPosition.top}px` : 'auto'};
    `};
`;
